/**
 * Simple slider with fade in/out including thumbnail slideshow
 */

import Player from '@vimeo/player';
import YouTubeIframeLoader from 'youtube-iframe';
import 'url-search-params-polyfill';

const ITEM_GUTTER = 15;
const MAX_WIDTH = 1160;
const TIMER = 4000;
const DEBOUNCE_TIME = 250;
const BREAKPOINT = 768;
const FIRST_SLIDE = 0;
const ITEMS_VISIBLE = 4;
const NEGATIVE_MULTIPLIER = -1;
const ITERATOR = 1;
const YT_ENDED = 0;
const YT_PLAYING = 1;
const YT_PAUSED = 2;

class Slider {
  currSlide = FIRST_SLIDE;
  thumbWidth = FIRST_SLIDE;
  offset = FIRST_SLIDE;
  isSlideshow = false;
  isAnimate = true;
  videoIsPlaying = false;
  itemsVisible = ITEMS_VISIBLE;
  slides = document.querySelectorAll('.js-slide');
  slider = document.querySelector('.js-slider');
  thumbEls = document.querySelectorAll('.js-slider-thumb');
  thumbsWrap = document.querySelector('.js-slider-thumbs');

  constructor() {
    if (this.slider && this.slides) {
      const prev = document.querySelector('.js-slider-prev');
      const next = document.querySelector('.js-slider-next');
      this.isSlideshow = this.thumbEls.length && this.thumbsWrap;

      this.goToSlide(FIRST_SLIDE);

      let autoRotate = this.rotate();

      this.handleVimeoEvents();
      this.handleYouTubeEvents();
      this.enablePauseOnHover(autoRotate);

      if (prev && next) {
        prev.addEventListener('click', () => this.prevSlide());
        next.addEventListener('click', () => this.nextSlide());
      }

      if (this.isSlideshow) {
        this.resizeThumbs();

        [].slice.call(this.thumbEls).forEach((item, i) => {
          item.addEventListener('click', () => {
            this.goToSlide(i);
            clearInterval(autoRotate);
            autoRotate = this.rotate();
          });
        });

        let resizeTimer;
        window.addEventListener('resize', () => {
          this.isAnimate = false;
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            this.resizeThumbs();
            this.isAnimate = true;
          }, DEBOUNCE_TIME);

        });
      }
    }
  }

  /**
   * Auto rotation
   * @returns {number}
   */
  rotate() {
    return setInterval(() => {
      if (this.currSlide <= this.slides.length) {
        this.nextSlide();
      } else {
        this.goToSlide(FIRST_SLIDE);
      }
    }, TIMER);
  }

  /**
   * Go to next slide
   */
  nextSlide() {
    this.goToSlide(this.currSlide + ITERATOR);
  }

  /**
   * Go to prev slide
   */
  prevSlide() {
    this.goToSlide(this.currSlide - ITERATOR);
  }

  /**
   * Go to a given slide
   * @param slide
   */
  goToSlide(slide) {
    this.slides[this.currSlide].classList.remove('is-visible');
    this.currSlide = (slide + this.slides.length) % this.slides.length;
    this.slides[this.currSlide].classList.add('is-visible');

    if (this.isSlideshow) {
      this.animateSlideshow();
    }
  }

  /**
   * Animate slideshow
   */
  animateSlideshow() {
    if (this.isAnimate) {
      if (this.currSlide > (this.itemsVisible - ITERATOR)) {
        this.offset = (this.currSlide - (this.itemsVisible - ITERATOR)) * this.thumbWidth * NEGATIVE_MULTIPLIER;
      } else {
        this.offset = 0;
      }

      Object.assign(this.thumbsWrap.style, {
        transform: 'translate3d(' + this.offset + 'px, 0, 0)',
        transition: 'transform 300ms ease-in'
      });
    }
  }

  /**
   * Resize slideshow thumbnails
   */
  resizeThumbs() {
    this.itemsVisible = 4;
    let wrapperWidth = document.body.clientWidth;

    if (document.body.clientWidth <= BREAKPOINT) {
      this.itemsVisible = 3;
    }

    if (wrapperWidth > MAX_WIDTH) {
      wrapperWidth = MAX_WIDTH;
    }

    wrapperWidth = wrapperWidth - ITEM_GUTTER;
    this.thumbWidth = (wrapperWidth / this.itemsVisible);
    this.thumbsWrap.style.width = this.thumbWidth * this.thumbEls.length + 'px';
    [].slice.call(this.thumbEls).forEach((thumb) => {
      thumb.setAttribute('style', 'width: ' + this.thumbWidth + 'px');
    });
  }

  enablePauseOnHover(autoRotate) {
    [].slice.call(this.slides).forEach((slide) => {
      // pause on hover
      slide.addEventListener('mouseover', () => clearInterval(autoRotate));

      // resume on mouse out
      slide.addEventListener('mouseout', () => {
        if (!this.videoIsPlaying) {
          autoRotate = this.rotate();
        }
      });
    });
  }

  handleYouTubeEvents() {
    const iframes = this.slider.querySelectorAll('iframe');
    [].slice.call(iframes).forEach((iframe) => {
      let src = iframe.getAttribute('src');
      if (src.indexOf('youtube') !== NEGATIVE_MULTIPLIER) {
        const playerId = 'yt-' + Math.random();
        if (src.indexOf('?') !== NEGATIVE_MULTIPLIER) {
          const params = new URLSearchParams(src);
          params.set('enablejsapi', ITERATOR);
          src = decodeURIComponent(params.toString())
        } else {
          src = src + '?enablejsapi=1';
        }
        iframe.setAttribute('src', src);
        iframe.setAttribute('id', playerId);
        YouTubeIframeLoader.load((YT) => {
          const player = new YT.Player(playerId, {
            events: {
              'onStateChange': () => {
                const state = player.getPlayerState();
                if (state === YT_PLAYING) {
                  this.videoIsPlaying = true;
                }

                if (state === YT_ENDED || state === YT_PAUSED) {
                  this.videoIsPlaying = false;
                }
              }
            }
          });
        })
      }
    })
  }

  handleVimeoEvents() {
    const iframes = this.slider.querySelectorAll('iframe');
    [].slice.call(iframes).forEach((iframe) => {
      if (iframe.getAttribute('src').indexOf('vimeo') !== NEGATIVE_MULTIPLIER) {
        const player = new Player(iframe);

        player.on('play', () => {
          this.videoIsPlaying = true;
        });

        player.on('pause', () => {
          this.videoIsPlaying = false;
        });

        player.on('ended', () => {
          this.videoIsPlaying = false;
        });

      }
    });
  }
}

export default Slider;
